'use client';

import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  theme?: 'customer' | 'provider';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', fullWidth, theme = 'customer', children, disabled, ...props }, ref) => {
    const isCustomer = theme === 'customer';

    const base = 'inline-flex items-center justify-center font-semibold rounded-2xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#FAF7F2]';

    const variants = {
      primary: isCustomer
        ? 'bg-customer-700 text-white shadow-[0_12px_28px_rgba(84,43,46,0.20)] hover:bg-customer-800 hover:-translate-y-0.5 focus:ring-customer-400'
        : 'bg-provider-700 text-white shadow-[0_12px_28px_rgba(66,39,74,0.20)] hover:bg-provider-800 hover:-translate-y-0.5 focus:ring-provider-400',
      secondary: 'bg-white/70 text-ink-900 border border-[#E8DDD5] shadow-sm hover:bg-white hover:border-[#D7C7BD] focus:ring-champagne-300',
      outline: isCustomer
        ? 'border border-customer-300 bg-white/60 text-customer-700 hover:bg-customer-50 hover:border-customer-400 focus:ring-customer-300'
        : 'border border-provider-300 bg-white/60 text-provider-700 hover:bg-provider-50 hover:border-provider-400 focus:ring-provider-300',
      ghost: 'text-gray-700 hover:bg-white/70 hover:text-ink-900 focus:ring-champagne-300',
    };

    const sizes = {
      sm: 'h-9 px-4 text-sm',
      md: 'h-12 px-6 text-base',
      lg: 'h-14 px-8 text-lg',
    };

    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
